import React, { useState, useEffect } from "react";
import { FaTimes, FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";

const GlossaireSidebar = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("glossaire");
  const [expandedItems, setExpandedItems] = useState({});

  // Termes de glossaire
  const glossaryTerms = [
    {
      term: "Enrôlement",
      definition:
        "Processus d'enregistrement d'un véhicule dans le système pour le suivi des documents et la gestion des transports.",
    },
    {
      term: "Immatriculation",
      definition:
        "Numéro d'identification officiel du véhicule attribué par les autorités.",
    },
    {
      term: "Carte transport",
      definition: "Document officiel autorisant le transport de marchandises.",
    },
    {
      term: "Statut 'En cours'",
      definition:
        "Indique que l'enrôlement n'est pas terminé et est encore en cours d'enregistrement.",
    },
    {
      term: "Statut 'Enrôlé'",
      definition:
        "Indique que l'enrôlement est terminé et valide pour le suivi des documents et la gestion des tours.",
    },
    {
      term: "Terminer un enrôlement",
      definition:
        "Action qui valide définitivement l'enrôlement d'un véhicule.",
    },
    {
      term: "Mise en corbeille",
      definition:
        "Action qui archive un enrôlement sans le supprimer définitivement.",
    },
    {
      term: "Carte grise",
      definition: "Document officiel attestant de l'identité d'un véhicule.",
    },
    {
      term: "CT nationale",
      definition:
        "Carte de transport nationale autorisant le transport de marchandises.",
    },
    {
      term: "CT internationale",
      definition:
        "Carte de transport internationale autorisant le transport de marchandises.",
    },
    {
      term: "Assurance",
      definition:
        "Contrat couvrant les risques liés à l'utilisation du véhicule.",
    },
    {
      term: "TVM",
      definition:
        "Taxe sur les Véhicules de Marchandises, due annuellement pour les poids lourds.",
    },
    {
      term: "Visite technique",
      definition: "Contrôle périodique obligatoire de l'état du véhicule.",
    },
    {
      term: "Tour national",
      definition: "Transport de marchandises effectué à l'intérieur du pays.",
    },
    {
      term: "Camion articulé",
      definition: "Véhicule composé d'un tracteur et d'une remorque.",
    },
    {
      term: "Camion non articulé",
      definition: "Véhicule rigide sans remorque.",
    },
    {
      term: "Charge utile",
      definition:
        "Poids maximum que peut transporter un véhicule, conformément à sa carte grise.",
    },
    {
      term: "Droit de tour",
      definition:
        "Taxe payée pour participer au système de rotation des transports.",
    },
    {
      term: "Droit de chargement",
      definition: "Taxe payée pour le chargement des marchandises.",
    },
    {
      term: "FIFO",
      definition:
        "First In First Out - système de rotation où les premiers arrivés sont les premiers servis.",
    },
    {
      term: "Liste de chargement",
      definition:
        "Liste des camions actuellement en cours de chargement de marchandises.",
    },
    {
      term: "Fret",
      definition:
        "Marchandise transportée par un camion ou un ensemble de camions.",
    },
    {
      term: "Fret national",
      definition: "Transport de marchandises effectué à l'intérieur du pays.",
    },
    {
      term: "Fret international",
      definition: "Transport de marchandises effectué entre différents pays.",
    },
    {
      term: "Quotas nationaux",
      definition:
        "Nombre de camions réservés pour les transporteurs nationaux.",
    },
    {
      term: "Quotas internationaux",
      definition:
        "Nombre de camions réservés pour les transporteurs internationaux.",
    },
    {
      term: "Charge utile",
      definition:
        "Poids maximum que peut transporter un camion, utilisé pour calculer le nombre de camions nécessaires.",
    },
    {
      term: "Réservation de transport",
      definition:
        "Processus de planification et d'affectation des ressources pour un transport spécifique.",
    },
    {
      term: "Assignation de camions",
      definition:
        "Processus d'affectation des camions disponibles à un fret spécifique.",
    },
  ];

  // Légende des statuts (badges)
  const statusLegends = [
    {
      term: "Statuts des pièces",
      badges: [
        {
          className: "badge bg-primary",
          text: "Valide",
          description:
            "La pièce est valide et il reste plus de 30 jours avant son expiration",
        },
        {
          className: "badge badge-warning",
          text: "30 jours",
          description:
            "La pièce est valide mais il reste moins de 30 jours avant son expiration.",
        },
        {
          className: "badge badge-warning",
          text: "Demain",
          description:
            "La pièce expire le lendemain, renouvellement urgent nécessaire",
        },
        {
          className: "badge badge-warning",
          text: "Ce jour",
          description:
            "C'est le dernier jour de validité. La pièce expire aujourd'hui, renouvellement immédiat requis",
        },
        {
          className: "badge bg-danger",
          text: "Expirée",
          description:
            "La pièce est expirée, il est nécessaire de la renouveler",
        },
      ],
    },
    {
      term: "Statuts des enrôlements",
      badges: [
        {
          className: "badge bg-primary",
          text: "Enrôlé",
          description:
            "L'enrôlement est terminé et valide pour le suivi des documents et la gestion des tours.",
        },
        {
          className: "badge badge-warning",
          text: "En cours",
          description:
            "L’enrôlement est en cours. Certaines informations sont encore en attente d’enregistrement.",
        },
      ],
    },
    {
      term: "Statuts des camions",
      badges: [
        {
          className: "badge badge-secondary",
          text: "Disponible",
          description:
            "Le camion est enregistré dans le système de tours et est prêt à transporter du fret.",
        },
        {
          className: "badge badge-warning",
          text: "En panne",
          description:
            "Le camion, auparavant disponible, est temporairement hors service.",
        },
        {
          className: "badge bg-danger",
          text: "Sorti de rang",
          description:
            "Le conducteur est momentanément indisponible pour une raison quelconque.",
        },
        {
          className: "badge bg-success",
          text: "Chargement de marchandises",
          description:
            "Le camion est actuellement en cours de chargement pour le transport du fret.",
        },
        {
          className: "badge bg-primary",
          text: "Transport de marchandises",
          description: "Le camion a quitté le parc avec le fret chargé.",
        },
      ],
    },
    {
      term: "Statuts de paiement",
      badges: [
        {
          className: "badge badge-secondary",
          text: "Non Payé",
          description:
            "Le droit de tour ou de chargement n'a pas encore été payé.",
        },
        {
          className: "badge bg-success",
          text: "Payé",
          description: "Le droit de tour ou de chargement a été payé.",
        },
      ],
    },
    {
      term: "Statuts des frets",
      badges: [
        {
          className: "badge badge-secondary",
          text: "En attente réservation",
          description: "Le fret est déclaré mais pas encore réservé.",
        },
        {
          className: "badge badge-warning",
          text: "En attente assignation",
          description:
            "Réservation effectuée, en attente d'assignation des camions.",
        },
        {
          className: "badge bg-success",
          text: "Assignation en cours",
          description: "Certains camions sont assignés, mais pas tous.",
        },
        {
          className: "badge bg-primary",
          text: "Assignation terminée",
          description: "Tous les camions nécessaires ont été assignés.",
        },
      ],
    },
    {
      term: "Statuts des camions assignés",
      badges: [
        {
          className: "badge badge-secondary",
          text: "Disponible",
          description: "Le camion est enregistré et prêt à transporter.",
        },
        {
          className: "badge bg-primary",
          text: "Assigné",
          description: "Le camion a été assigné à un fret spécifique.",
        },
        {
          className: "badge bg-success",
          text: "En route",
          description: "Le camion a quitté le parc avec le fret chargé.",
        },
        {
          className: "badge bg-danger",
          text: "Sorti",
          description: "Le camion a terminé sa mission.",
        },
      ],
    },
  ];

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredTerms = glossaryTerms.filter(
    (term) =>
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStatusLegends = statusLegends
    .map((legend) => {
      // Si le terme principal correspond à la recherche, on garde tout
      if (legend.term.toLowerCase().includes(searchTerm.toLowerCase())) {
        return legend;
      }

      // Sinon, on filtre les badges
      const filteredBadges = legend.badges.filter(
        (badge) =>
          badge.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
          badge.description.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Si on a des badges qui correspondent, on retourne une nouvelle légende avec seulement ces badges
      if (filteredBadges.length > 0) {
        return {
          ...legend,
          badges: filteredBadges,
        };
      }

      // Sinon, on retourne null (sera filtré ensuite)
      return null;
    })
    .filter(Boolean); // On enlève les entrées null

  // Bloquer le scroll de la page principale
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Styles inline
  const styles = {
    sidebar: {
      position: "fixed",
      top: 0,
      right: isOpen ? 0 : "-400px",
      width: "400px",
      height: "100vh", // Utilisez 100vh au lieu de 100%
      backgroundColor: "white",
      boxShadow: "-2px 0 10px rgba(0, 0, 0, 0.1)",
      transition: "right 0.3s ease",
      zIndex: 1050,
      overflowY: "auto",
      overscrollBehavior: "contain", // Empêche le scroll de se propager à l'arrière-plan
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1040,
      display: isOpen ? "block" : "none",
      overscrollBehavior: "none", // Empêche le scroll sur l'overlay
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px",
      borderBottom: "1px solid #eee",
    },
    tabs: {
      display: "flex",
      borderBottom: "1px solid #dee2e6",
    },
    tab: {
      padding: "10px 15px",
      cursor: "pointer",
      borderBottom: "3px solid transparent",
    },
    activeTab: {
      borderBottom: "3px solid #007bff",
      fontWeight: "bold",
    },
    closeBtn: {
      background: "none",
      border: "none",
      fontSize: "1.2rem",
      cursor: "pointer",
    },
    search: {
      padding: "15px",
      borderBottom: "1px solid #eee",
    },
    content: {
      padding: "15px",
    },
    item: {
      marginBottom: "15px",
      borderBottom: "1px solid #f5f5f5",
      paddingBottom: "10px",
      borderRadius: "4px",
      transition: "all 0.2s ease",
    },
    termHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      padding: "8px",
      "&:hover": {
        backgroundColor: "#f8f9fa",
      },
    },
    termTitle: {
      margin: 0,
      color: "#333",
      flex: 1,
      marginBottom: "15px",
    },
    termContent: {
      marginTop: "10px",
      padding: "12px",
      backgroundColor: "#f8f9fa",
      borderRadius: "4px",
      borderLeft: "3px solid rgb(110, 110, 110)",
    },
    termDefinition: {
      marginBottom: "5px",
    },
    termExamples: {
      fontSize: "0.8rem",
      color: "#6c757d",
      fontStyle: "italic",
    },
    badgeItem: {
      margin: "10px 0",
      display: "flex",
      flexDirection: "column",
    },
    badgeHeader: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      padding: "8px",
      "&:hover": {
        backgroundColor: "#f8f9fa",
      },
    },
    badgeContent: {
      marginTop: "8px",
      padding: "12px",
      backgroundColor: "#f8f9fa",
      borderRadius: "4px",
      borderLeft: "3px solid rgb(110, 110, 110)",
    },
    badgeExample: {
      display: "inline-block",
      padding: "3px 8px",
      borderRadius: "10px",
      marginRight: "10px",
      fontSize: "0.8rem",
      minWidth: "100px",
      textAlign: "center",
      color: "white",
      // Retirez flexShrink: 0 pour permettre le rétrécissement si nécessaire
    },
    inputGroup: {
      display: "flex",
      width: "100%",
    },
    searchInput: {
      flex: 1,
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px 0 0 4px",
    },
    searchIcon: {
      padding: "8px 12px",
      border: "1px solid #ddd",
      borderLeft: "none",
      borderRadius: "0 4px 4px 0",
      backgroundColor: "#f8f9fa",
    },
    expandIcon: {
      marginLeft: "10px",
      fontSize: "0.8rem",
    },
  };

  // Styles pour mobile
  if (window.innerWidth <= 768) {
    styles.sidebar.width = "80%";
    styles.sidebar.right = isOpen ? 0 : "-100%";
  }

  // Fonction pour obtenir la couleur correspondante
  const getBadgeColor = (colorClass) => {
    switch (colorClass) {
      case "bg-success":
        return "#28a745";
      case "bg-warning":
        return "#ffc107";
      case "bg-danger":
        return "#dc3545";
      case "bg-secondary":
        return "#6c757d";
      default:
        return "#007bff";
    }
  };

  return (
    <>
      <div style={styles.overlay} onClick={onClose} />

      <div style={styles.sidebar}>
        <div style={styles.header}>
          <h4>Aide</h4>
          <button onClick={onClose} style={styles.closeBtn}>
            <FaTimes />
          </button>
        </div>

        <div style={styles.tabs}>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === "glossaire" ? styles.activeTab : {}),
            }}
            onClick={() => setActiveTab("glossaire")}
          >
            Glossaire
          </div>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === "statuts" ? styles.activeTab : {}),
            }}
            onClick={() => setActiveTab("statuts")}
          >
            Légende des statuts
          </div>
        </div>

        <div style={styles.search}>
          <div style={styles.inputGroup}>
            <input
              type="text"
              placeholder={`Rechercher ${
                activeTab === "glossaire" ? "un terme" : "un statut"
              }...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <span style={styles.searchIcon}>
              <FaSearch />
            </span>
          </div>
        </div>

        <div style={styles.content}>
          {activeTab === "glossaire" ? (
            filteredTerms.length > 0 ? (
              filteredTerms.map((item, index) => {
                const isTermExpanded = expandedItems[`term-${index}`];
                return (
                  <div key={index} style={styles.item}>
                    <div
                      style={styles.termHeader}
                      onClick={() => toggleExpand(`term-${index}`)}
                    >
                      <h5 style={styles.termTitle}>{item.term}</h5>
                      {isTermExpanded ? (
                        <FaChevronUp style={styles.expandIcon} />
                      ) : (
                        <FaChevronDown style={styles.expandIcon} />
                      )}
                    </div>

                    {isTermExpanded && (
                      <div style={styles.termContent}>
                        <div style={styles.termDefinition}>
                          {item.definition}
                        </div>
                        {/* {item.examples && (
                          <div style={styles.termExamples}> {item.examples}</div>
                        )} */}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p>Aucun terme trouvé.</p>
            )
          ) : // ) : (
          //     filteredStatusLegends.length > 0 ? (
          //         filteredStatusLegends.map((legend, index) => (
          //             <div key={index} style={styles.item}>
          //                 <h5 style={styles.termTitle}>{legend.term}</h5>
          //                 {legend.badges.map((badge, badgeIndex) => {
          //                     const isBadgeExpanded = expandedItems[`badge-${index}-${badgeIndex}`];
          //                     return (
          //                         <div key={badgeIndex} style={styles.badgeItem}>
          //                             <div
          //                                 style={styles.badgeHeader}
          //                                 onClick={() => toggleExpand(`badge-${index}-${badgeIndex}`)}
          //                             >
          //                                 {/* <span
          //                                     style={{
          //                                         ...styles.badgeExample,
          //                                         backgroundColor: getBadgeColor(badge.color),
          //                                         color: badge.color.includes('warning') ? '#212529' : 'white'
          //                                     }}
          //                                 >
          //                                     {badge.text}
          //                                 </span> */}
          //                                 <span
          //                                     className={badge.className}
          //                                     style={{
          //                                         fontSize: '0.8rem',
          //                                         textAlign: 'center'
          //                                     }}
          //                                 >
          //                                     {badge.text}
          //                                 </span>
          //                                 {isBadgeExpanded ?
          //                                     <FaChevronUp style={styles.expandIcon} /> :
          //                                     <FaChevronDown style={styles.expandIcon} />}
          //                             </div>

          //                             {isBadgeExpanded && (
          //                                 <div style={styles.badgeContent}>
          //                                     <div style={styles.badgeDescription}>{badge.description}</div>
          //                                     {badge.examples && (
          //                                         <div style={styles.badgeExamples}> {badge.examples}</div>
          //                                     )}
          //                                 </div>
          //                             )}
          //                         </div>
          //                     );
          //                 })}
          //             </div>
          //         ))
          //     ) : (
          //         <p>Aucun statut trouvé.</p>
          //     )
          filteredStatusLegends.length > 0 ? (
            filteredStatusLegends.map((legend, index) => (
              <div key={index} style={styles.item}>
                <h5 style={{ ...styles.termTitle, marginBottom: "10px" }}>
                  {legend.term}
                </h5>
                <table className="table table-bordered table-sm">
                  <tbody>
                    {legend.badges.map((badge, badgeIndex) => (
                      <tr key={badgeIndex}>
                        <td
                          style={{
                            width: "20%",
                            padding: "5px",
                            verticalAlign: "middle",
                          }}
                        >
                          <span
                            className={`${badge.className}`}
                            style={{
                              padding: "2px 5px",
                              display: "inline-block",
                            }}
                          >
                            {badge.text}
                          </span>
                        </td>
                        <td
                          style={{
                            width: "75%",
                            padding: "5px",
                            verticalAlign: "middle",
                          }}
                        >
                          <small>{badge.description}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          ) : (
            <p>Aucun statut trouvé.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default GlossaireSidebar;
